import React, { useEffect } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useSelector } from 'react-redux';

import { fetchAppVersion } from 'src/features/appVersion';
import { actions as authActions, selectors as authSelectors } from 'src/features/auth/authSlice';
import { fetchCurrentUser } from 'src/features/auth/services';
import { getFeatureFlagIsIdle, getFeatureFlagIsLoading } from 'src/features/featureFlag';
import { useGetFeatureFlag } from 'src/features/featureFlag/hooks/useGetFeatureFlag';
import { selectors as propertySelectors } from 'src/features/property';
import { configVars } from 'src/helpers';
import Router from 'src/router';
import { useAppDispatch } from 'src/store';
import { FeatureFlagKey } from 'src/ts/enums';
import { authApi } from './api';
import useProperty from './features/property/hooks/useProperty';

const COOKIE_MIGRATE_KEY = 'fraud_platform_cookie_migrate';
const isCookieMigrated = () => localStorage.getItem(COOKIE_MIGRATE_KEY) != null;
const markMigrated = () => localStorage.setItem(COOKIE_MIGRATE_KEY, Date.now().toString());

const App: React.FC = () => {
  const dispatch = useAppDispatch();

  const userData = useSelector(authSelectors.user);
  const userIsIdle = useSelector(authSelectors.isIdle);
  const userIsLoading = useSelector(authSelectors.isLoading);

  const featureFlagIsIdle = useSelector(getFeatureFlagIsIdle);
  const featureFlagIsLoading = useSelector(getFeatureFlagIsLoading);

  const { onGetIdentityVerificationPropertyEnabled } = useProperty();

  const getIdentityVerificationPropertyEnabledIsIdle = useSelector(
    propertySelectors.hasIdentityVerificationPropertyEnabled.isIdle
  );
  useEffect(() => {
    if (userData && getIdentityVerificationPropertyEnabledIsIdle) {
      onGetIdentityVerificationPropertyEnabled();
    }
  }, [
    userData,
    getIdentityVerificationPropertyEnabledIsIdle,
    onGetIdentityVerificationPropertyEnabled
  ]);

  const hasIdentityVerificationPropertyEnabled = useSelector(
    propertySelectors.hasIdentityVerificationPropertyEnabled.data
  );

  useEffect(() => {
    const authRefresh = async () => {
      if (userData && !isCookieMigrated()) {
        const { pathname } = window.location;
        markMigrated();
        if (pathname === '/login') {
          dispatch(authActions.logout());
        } else {
          await authApi.refreshSession();
          dispatch(fetchCurrentUser({ isRefreshSessionCaller: true }));
        }
      }
    };
    authRefresh().catch(console.error);
  }, [userData, dispatch]);

  useEffect(() => {
    if (userIsIdle && !userData) {
      dispatch(fetchCurrentUser());
      dispatch(fetchAppVersion());
    }
  }, [userIsIdle, userData, dispatch]);

  const { onGetFeatureFlagValue } = useGetFeatureFlag();

  useEffect(() => {
    if (!userIsIdle && !userIsLoading && userData) {
      onGetFeatureFlagValue(
        FeatureFlagKey.ANALYTICS_AND_REPORTING_NAV,
        `user=${userData.short_id},role=${userData.role}`
      );
      onGetFeatureFlagValue(
        FeatureFlagKey.SALESFORCE_EMBEDDED_MESSAGING,
        `user=${userData.short_id},role=${userData.role}`
      );
      onGetFeatureFlagValue(
        FeatureFlagKey.CPN_FRAUD,
        `user=${userData.short_id},role=${userData.role}`
      );
      onGetFeatureFlagValue(FeatureFlagKey.SNAX_1331_SETTINGS_SERVICE_ENTITY_FEATURES);
      onGetFeatureFlagValue(
        FeatureFlagKey.ITL_2960_INSPECTOR_PANEL,
        `user=${userData.short_id},role=${userData.role}`
      );
      onGetFeatureFlagValue(
        FeatureFlagKey.ITL_3258_LIST_CHECKS_TAB,
        `user=${userData.short_id},role=${userData.role}`
      );
      onGetFeatureFlagValue(
        FeatureFlagKey.ITL_3152_REPORT_PLATFORM_FEEDBACK,
        `user=${userData.short_id},role=${userData.role}`
      );
      onGetFeatureFlagValue(
        FeatureFlagKey.ITL_3488_NO_MATCH_COMMENT,
        `user=${userData.short_id},role=${userData.role}`
      );
      onGetFeatureFlagValue(
        FeatureFlagKey.ITL_3287_SPECIALTY_CALCULATION_NOTE,
        `user=${userData.short_id},role=${userData.role}`
      );
      onGetFeatureFlagValue(
        FeatureFlagKey.AIC_1285_PARTNER_ADMIN_ROLE,
        `user=${userData.short_id},role=${userData.role}`
      );
    }
  }, [userIsIdle, userIsLoading, userData, onGetFeatureFlagValue]);

  useEffect(() => {
    const { pathname } = window.location;

    const isDocumentPortal = pathname.includes('application/apply');
    if (isDocumentPortal) {
      const favicon = document.getElementById('favicon') as HTMLAnchorElement;
      favicon.href = '/assets/images/DocumentIcon.svg';
      document.title = 'Document Portal';
    }
  });

  //Ensure load of the data required for the nav options to render the ID Verification Link tab correctly
  if (
    ((userIsIdle || userIsLoading) && (featureFlagIsIdle || featureFlagIsLoading)) ||
    (!userIsIdle && !userIsLoading && userData && hasIdentityVerificationPropertyEnabled === null)
  ) {
    return null;
  }

  return (
    <>
      {(configVars.heap.app_id || configVars.pendo?.apiKey) && (
        <HelmetProvider>
          <Helmet>
            {configVars.heap.app_id && (
              <script type="text/javascript">
                {`window.heapReadyCb=window.heapReadyCb||[],window.heap=window.heap||[],heap.load=function(e,t){window.heap.envId=e,window.heap.clientConfig=t=t||{},window.heap.clientConfig.shouldFetchServerConfig=!1;var a=document.createElement("script");a.type="text/javascript",a.async=!0,a.src="https://cdn.us.heap-api.com/config/"+e+"/heap_config.js";var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(a,r);var n=["init","startTracking","stopTracking","track","resetIdentity","identify","identifyHashed","getSessionId","getUserId","getIdentity","addUserProperties","addEventProperties","removeEventProperty","clearEventProperties","addAccountProperties","addAdapter","addTransformer","addTransformerFn","onReady","addPageviewProperties","removePageviewProperty","clearPageviewProperties","trackPageview"],i=function(e){return function(){var t=Array.prototype.slice.call(arguments,0);window.heapReadyCb.push({name:e,fn:function(){heap[e]&&heap[e].apply(heap,t)}})}};for(var p=0;p<n.length;p++)heap[n[p]]=i(n[p])};
    heap.load("${configVars.heap.app_id}");`}
              </script>
            )}
            {configVars.pendo?.apiKey && (
              <script>
                {`(function(apiKey){
                  (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];
                    v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){
                        o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
                        y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js';
                        z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');
                })("${configVars.pendo.apiKey}");`}
              </script>
            )}
          </Helmet>
        </HelmetProvider>
      )}
      <Router />
    </>
  );
};

export default App;
